import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight, GripHorizontal, X } from "lucide-react";

type Props = {
    title: string;
    children: ReactNode;
    initialX?: number;
    initialY?: number;
    /** Tailwind width class for the floating card. Defaults to a narrow card.
     *  Used until the user drag-resizes the panel, after which an explicit width
     *  takes over. */
    widthClassName?: string;
    /** When provided, renders an X button that fully closes the panel. */
    onClose?: () => void;
};

const MIN_W = 200;
const MIN_H = 160;

/** A small floating card the user can drag around by its header and resize from
 *  its bottom-right corner. Positioned absolutely within the nearest positioned
 *  ancestor (the board area). Its position is always clamped inside that
 *  ancestor, so resizing the layout (e.g. expanding the app sidebar) can never
 *  push it off-screen. */
export default function DraggablePanel({
    title,
    children,
    initialX = 16,
    initialY = 16,
    widthClassName = "w-60",
    onClose,
}: Props) {
    const { t } = useTranslation();
    const [pos, setPos] = useState({ x: initialX, y: initialY });
    const [collapsed, setCollapsed] = useState(false);
    // null until the user drag-resizes; then explicit pixel size takes over.
    const [size, setSize] = useState<{ w: number; h: number } | null>(null);
    const dragging = useRef(false);
    const resizing = useRef(false);
    const rootRef = useRef<HTMLDivElement>(null);

    // Keep (x, y) within the bounds of the positioned ancestor.
    const clamp = useCallback((x: number, y: number) => {
        const el = rootRef.current;
        const parent = el?.offsetParent as HTMLElement | null;
        if (!el || !parent) return { x: Math.max(0, x), y: Math.max(0, y) };
        const maxX = Math.max(0, parent.clientWidth - el.offsetWidth);
        const maxY = Math.max(0, parent.clientHeight - el.offsetHeight);
        return {
            x: Math.min(Math.max(0, x), maxX),
            y: Math.min(Math.max(0, y), maxY),
        };
    }, []);

    // Re-clamp whenever the container resizes (sidebar expand/collapse, window
    // resize, etc.).
    useEffect(() => {
        const el = rootRef.current;
        const parent = el?.offsetParent as HTMLElement | null;
        setPos((p) => clamp(p.x, p.y));
        if (!parent || typeof ResizeObserver === "undefined") return;
        const ro = new ResizeObserver(() => setPos((p) => clamp(p.x, p.y)));
        ro.observe(parent);
        return () => ro.disconnect();
    }, [clamp, collapsed]);

    // Clamp a requested (w, h) so the panel stays inside its positioned ancestor.
    const clampSize = useCallback((w: number, h: number) => {
        const el = rootRef.current;
        const parent = el?.offsetParent as HTMLElement | null;
        const maxW = parent ? parent.clientWidth - pos.x : w;
        const maxH = parent ? parent.clientHeight - pos.y : h;
        return {
            w: Math.min(Math.max(MIN_W, w), Math.max(MIN_W, maxW)),
            h: Math.min(Math.max(MIN_H, h), Math.max(MIN_H, maxH)),
        };
    }, [pos.x, pos.y]);

    const sized = size !== null;

    return (
        <div
            ref={rootRef}
            className={`absolute z-20 flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white/95 shadow-theme-lg backdrop-blur ${
                sized ? "" : widthClassName
            }`}
            style={
                sized
                    ? { left: pos.x, top: pos.y, width: size!.w, height: collapsed ? undefined : size!.h }
                    : { left: pos.x, top: pos.y }
            }
        >
            <div
                className="flex shrink-0 cursor-move touch-none items-center gap-1 border-b bg-gray-50 px-2 py-1.5"
                onPointerDown={(e) => {
                    dragging.current = true;
                    e.currentTarget.setPointerCapture(e.pointerId);
                }}
                onPointerMove={(e) => {
                    if (!dragging.current) return;
                    setPos((p) => clamp(p.x + e.movementX, p.y + e.movementY));
                }}
                onPointerUp={(e) => {
                    dragging.current = false;
                    e.currentTarget.releasePointerCapture(e.pointerId);
                }}
            >
                <GripHorizontal className="h-4 w-4 text-content-tertiary" />
                <span className="flex-1 truncate text-xs font-semibold text-gray-700">{title}</span>
                <button
                    type="button"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={() => setCollapsed((c) => !c)}
                    className="rounded p-0.5 text-gray-500 hover:bg-gray-200"
                    aria-label={t(collapsed ? "common.expand" : "common.collapse", collapsed ? "Expand" : "Collapse")}
                    aria-expanded={!collapsed}
                >
                    {collapsed ? (
                        <ChevronRight className="h-3.5 w-3.5" />
                    ) : (
                        <ChevronDown className="h-3.5 w-3.5" />
                    )}
                </button>
                {onClose && (
                    <button
                        type="button"
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={onClose}
                        className="rounded p-0.5 text-gray-500 hover:bg-gray-200"
                        aria-label={t("common.close")}
                    >
                        <X className="h-3.5 w-3.5" />
                    </button>
                )}
            </div>
            {!collapsed && (
                <div
                    className={`overflow-y-auto ${sized ? "min-h-0 flex-1" : "max-h-[60vh]"}`}
                >
                    {children}
                </div>
            )}
            {!collapsed && (
                <div
                    role="separator"
                    aria-label={`${title} resize`}
                    className="absolute bottom-0 right-0 h-4 w-4 cursor-se-resize touch-none text-content-tertiary"
                    onPointerDown={(e) => {
                        e.stopPropagation();
                        const el = rootRef.current;
                        if (!el) return;
                        resizing.current = true;
                        // Seed from current rendered size so the first drag is smooth.
                        const rect = el.getBoundingClientRect();
                        setSize(clampSize(rect.width, rect.height));
                        e.currentTarget.setPointerCapture(e.pointerId);
                    }}
                    onPointerMove={(e) => {
                        if (!resizing.current) return;
                        setSize((s) =>
                            clampSize((s?.w ?? MIN_W) + e.movementX, (s?.h ?? MIN_H) + e.movementY),
                        );
                    }}
                    onPointerUp={(e) => {
                        resizing.current = false;
                        e.currentTarget.releasePointerCapture(e.pointerId);
                    }}
                >
                    <svg viewBox="0 0 10 10" className="h-full w-full" aria-hidden>
                        <path d="M9 1 L9 9 L1 9" fill="none" stroke="currentColor" strokeWidth="1" />
                        <path d="M9 5 L5 9" fill="none" stroke="currentColor" strokeWidth="1" />
                    </svg>
                </div>
            )}
        </div>
    );
}
